import React, { Fragment, useState } from 'react';
import { FinancialAccountNode, OrgNode } from '../../services/orgs/interfaces';
import { StripeMetadataInput, SuccessData } from '../VaultForm';
import { StripeEnv } from '../../util/enums';
import Stepper, { StepperPage } from '../Stepper';
import ConfirmationPage from './ConfirmationPage';
import submit from './submit';
import useVgsCollect from '../VgsInput/hooks/useVgsCollect';
import { STATES, TITLES } from '../../const';
import Complete from '../Complete';
import { DEFAULT_OWNERSHIP_VALUE } from '../Ownership/const';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { ORG_CONFIRMATION_WITH_CREATE_KYC_SUBMIT_TREATMENT } from '../../split';
import { FieldError } from '../VgsInput/field';
import ErrorMessage from '../ErrorMessage';
import VaultSubmitError from '../../util/VaultSubmitError';
import logger from '../../util/logger';
import { BeneficialOwnerData } from '../BeneficialOwners';

export default function OrgConfirmationWithCreateKyc({
  kyb,
  setRefreshKyb,
  submittedOnYourBehalf,
  financialAccountNode,
  org,
  currentUserId,
  onSuccess,
  onRevealSuccess,
  onClose,
  onRevealFail,
  statementDescriptor,
  metadata,
  stripeEnv,
  provider,
  token,
  raiseConfig,
  bankAccountPayableOrgId,
  fundraiserId,
  fundraiserSlug,
  showRaiseLastStep,
  shadow,
  showFaq,
  showSubmitForReviewButton,
}: {
  kyb: Kyb;
  setRefreshKyb: (refresh: symbol) => void;
  submittedOnYourBehalf: boolean;
  financialAccountNode: FinancialAccountNode;
  org: OrgNode;
  currentUserId: string;
  onSuccess: (res: SuccessData) => void;
  onRevealSuccess: () => void;
  onClose?: () => void;
  onRevealFail: () => void;
  statementDescriptor?: string;
  metadata?: StripeMetadataInput;
  stripeEnv: StripeEnv;
  provider: VaultProvider;
  token: string;
  raiseConfig?: {
    raiseCampaignId?: string;
    raiseUserId?: string;
  };
  bankAccountPayableOrgId?: string;
  fundraiserId?: string | null;
  fundraiserSlug?: string | null;
  showRaiseLastStep?: boolean;
  shadow?: boolean;
  showFaq?: boolean;
  showSubmitForReviewButton?: boolean;
}): React.JSX.Element {
  const form = useVgsCollect<{ kycId: string }>();
  const [isValid, setIsValid] = useState(false);
  const [kycAddressRegion, setKycAddressRegion] = useState(STATES[0].value);
  const [kycPercentOwnership, setKycPercentOwnership] = useState(
    DEFAULT_OWNERSHIP_VALUE,
  );
  const [kycTitle, setKycTitle] = useState(TITLES[0].value);
  const [kycIsOwner, setKycIsOwner] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [beneficialOwnersData, setBeneficialOwnersData] = useState<
    BeneficialOwnerData[]
  >([]);
  const { isReady: splitIsReady, treatments: splitTreatments } =
    useSplitTreatments({
      names: [ORG_CONFIRMATION_WITH_CREATE_KYC_SUBMIT_TREATMENT.name],
      attributes: {
        orgId: org.id,
      },
    });
  const steps: StepperPage[] = [
    {
      component: (
        <Fragment>
          {form && (
            <ConfirmationPage
              kyb={kyb}
              submittedOnYourBehalf={submittedOnYourBehalf}
              onRevealSuccess={onRevealSuccess}
              onRevealFail={onRevealFail}
              form={form}
              onValidChange={setIsValid}
              onAddressRegionChange={setKycAddressRegion}
              onIsOwnerChange={setKycIsOwner}
              onPercentOwnershipChange={setKycPercentOwnership}
              onTitleChange={setKycTitle}
              token={token}
              raiseConfig={raiseConfig}
              fieldErrors={fieldErrors}
              setRefreshKyb={setRefreshKyb}
              onBeneficialOwnersChange={setBeneficialOwnersData}
              showSubmitForReviewButton={showSubmitForReviewButton}
            />
          )}
        </Fragment>
      ),
      isValid,
      navigation: { showBack: false, showForward: true, showSteps: true },
      nextText: 'Submit for Review',
      beforeNext: async (): Promise<void> => {
        setFieldErrors([]);
        setErrorMessage(undefined);
        if (form === undefined) {
          throw new Error('VGSForm not instantiated');
        }
        if (!splitIsReady) {
          throw new Error('Split is not ready');
        }
        try {
          const successData = await submit({
            stripeEnv,
            kyb,
            submittedOnYourBehalf,
            financialAccountNode,
            org,
            currentUserId,
            statementDescriptor,
            metadata,
            kycAddressRegion,
            kycPercentOwnership,
            kycIsOwner,
            kycTitle,
            form,
            provider,
            splitTreatments,
            bankAccountPayableOrgId,
            fundraiserId,
            fundraiserSlug,
            beneficialOwnersData,
          });
          logger(
            `OrgConfirmationWithCreateKyc successData ${JSON.stringify(successData)}`,
          );
          setTimeout(() => {
            onSuccess(successData);
          }, 10 * 1000);
        } catch (e) {
          if (e instanceof VaultSubmitError) {
            if (e.field !== undefined && e.fieldMessage !== undefined) {
              setFieldErrors([
                {
                  fieldName: e.field,
                  message: e.fieldMessage,
                },
              ]);
            }

            setErrorMessage(e.message);
          } else {
            setErrorMessage(`Unknown error: ${e} ${JSON.stringify(e)}`);
          }
        }
      },
      onCancel: onClose,
      srName: 'Confirm KYB/KYC Details',
      shouldRender: true,
    },
    {
      component:
        errorMessage !== undefined ? (
          <ErrorMessage
            message={errorMessage}
            showThankYou={fieldErrors.length === 0}
          />
        ) : (
          <Complete onClose={onClose} />
        ),
      isValid: true,
      navigation: {
        showBack: fieldErrors.length > 0,
        showForward: false,
        showSteps: !showRaiseLastStep || !errorMessage,
      },
      shouldRender: !showRaiseLastStep || !!errorMessage,
      srName:
        errorMessage !== undefined
          ? 'Confirm KYB/KYC Errors'
          : 'Confirm KYB/KYC Completed',
    },
  ];
  // requires the user to be able to submit new kyc
  return (
    <Stepper
      pages={steps}
      showSubmitForReviewButton={showSubmitForReviewButton}
      shadow={shadow}
      showFaq={showFaq}
    />
  );
}
